// Espace documentaire & GED — Gestion Electronique de Documents
import { useState, useEffect, useCallback, useRef } from 'react';
import api, { getToken } from '../services/api';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ── Secure download — uses auth header, no direct file_url exposure ───
async function downloadDocument(id, filename) {
  try {
    const res = await fetch(`${BASE_URL}/ged/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) { alert('Téléchargement impossible ou accès refusé'); return; }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch { alert('Erreur lors du téléchargement'); }
}

// ── Helpers ──────────────────────────────────────────────────────────
function formatBytes(b) {
  if (!b) return '0 B';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}
function daysLeft(ds) {
  if (!ds) return null;
  return Math.ceil((new Date(ds) - new Date()) / 86400000);
}

const STATUS_C = {
  actif:      { bg: '#d1fae5', text: '#065f46' },
  expiré:     { bg: '#fee2e2', text: '#991b1b' },
  résilié:    { bg: '#f3f4f6', text: '#4b5563' },
  en_attente: { bg: '#fef3c7', text: '#92400e' },
  archivé:    { bg: '#e5e7eb', text: '#374151' },
};
const ENTITY_LABELS = {
  client: 'Client', fournisseur: 'Fournisseur', agent_commercial: 'Agent commercial',
  employe: 'Employé', société: 'Société', autre: 'Autre',
};

async function uploadFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE_URL}/ged/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload échoué');
  return data;
}

// ── Shared UI ─────────────────────────────────────────────────────────
function KpiCard({ title, value, sub, color = '#1a9e72', icon }) {
  return (
    <div style={{ background: '#ffffff', borderRadius: 10, padding: '16px 20px', borderLeft: `3px solid ${color}`, flex: 1, minWidth: 130 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ color: '#4b5563', fontSize: 11, marginBottom: 4 }}>{title}</div>
          <div style={{ color: '#111827', fontSize: 24, fontWeight: 700 }}>{value}</div>
          {sub && <div style={{ color: '#4b5563', fontSize: 11, marginTop: 2 }}>{sub}</div>}
        </div>
        {icon && <span style={{ fontSize: 20, opacity: 0.7 }}>{icon}</span>}
      </div>
    </div>
  );
}
function Badge({ status }) {
  const c = STATUS_C[status] || { bg: '#f3f4f6', text: '#4b5563' };
  return <span style={{ background: c.bg, color: c.text, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{status}</span>;
}
function ExpiryBadge({ date }) {
  const d = daysLeft(date);
  if (d === null) return null;
  const color = d < 0 ? '#ef4444' : d < 30 ? '#f97316' : d < 60 ? '#eab308' : '#22c55e';
  const label = d < 0 ? `Expiré il y a ${Math.abs(d)}j` : d === 0 ? "Expire aujourd'hui" : `${d}j`;
  return <span style={{ color, fontSize: 11, fontWeight: 600, background: color + '18', padding: '2px 7px', borderRadius: 20 }}>{label}</span>;
}

// ── Shared input helpers ──────────────────────────────────────────────
const S = {
  input: { width: '100%', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, color: '#111827', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' },
  label: { color: '#4b5563', fontSize: 11, display: 'block', marginBottom: 3 },
};

// ── TAB 1 — Dashboard ─────────────────────────────────────────────────
function DashboardTab({ companyId, goTab }) {
  const [stats, setStats]   = useState(null);
  const [loading, setLoad]  = useState(true);

  useEffect(() => {
    setLoad(true);
    api.ged.stats(companyId ? { company_id: companyId } : {}).then(setStats).finally(() => setLoad(false));
  }, [companyId]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#4b5563' }}>Chargement…</div>;
  if (!stats)  return null;

  const usedMB  = (stats.storageBytes || 0) / 1048576;
  const quotaMB = 500;
  const pct     = Math.min(100, (usedMB / quotaMB) * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <KpiCard title="Documents actifs"  value={stats.total}      color="#1a9e72" icon="📁" />
        <KpiCard title="Expire dans 30j"   value={stats.expiring30} color="#f97316" icon="⚠️" sub={stats.expiring30 > 0 ? 'Action requise' : ''} />
        <KpiCard title="Expirés"           value={stats.expired}    color="#ef4444" icon="🚨" sub={stats.expired > 0 ? 'À renouveler' : ''} />
        <KpiCard title="En attente sig."   value={stats.pendingSig} color="#8b5cf6" icon="✍️" />
        <KpiCard title="Stockage"          value={formatBytes(stats.storageBytes)} color="#185fa5" icon="💾" sub={`${pct.toFixed(1)}% / ${quotaMB} MB`} />
      </div>

      {pct > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#4b5563', marginBottom: 4 }}>
            <span>Espace utilisé</span><span>{formatBytes(stats.storageBytes)} / {quotaMB} MB</span>
          </div>
          <div style={{ height: 5, background: '#e5e7eb', borderRadius: 3 }}>
            <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: pct > 85 ? '#ef4444' : pct > 60 ? '#f97316' : '#1a9e72', transition: 'width .5s' }} />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 300 }}>
          <div style={{ color: '#4b5563', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Alertes d'expiration (90j)</div>
          {stats.alerts.length === 0
            ? <div style={{ background: '#ffffff', borderRadius: 8, padding: '14px 16px', color: '#4b5563', fontSize: 13 }}>Aucune alerte dans les 90 prochains jours.</div>
            : stats.alerts.map(doc => (
              <div key={doc.id} style={{ background: '#ffffff', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, borderLeft: `3px solid ${doc.days_left < 0 ? '#ef4444' : doc.days_left < 30 ? '#f97316' : '#eab308'}` }}>
                <span style={{ fontSize: 18 }}>{doc.category_icon || '📄'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#111827', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</div>
                  <div style={{ color: '#4b5563', fontSize: 11 }}>{doc.category_name}{doc.linked_entity_name ? ` · ${doc.linked_entity_name}` : ''}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <ExpiryBadge date={doc.expiry_date} />
                  <div style={{ color: '#4b5563', fontSize: 10, marginTop: 2 }}>{fmtDate(doc.expiry_date)}</div>
                </div>
              </div>
            ))
          }
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ color: '#4b5563', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Par catégorie</div>
          {stats.byCategory.filter(c => c.count > 0).slice(0, 10).map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ffffff', borderRadius: 6, padding: '7px 10px', marginBottom: 4 }}>
              <span style={{ fontSize: 14 }}>{c.icon || '📁'}</span>
              <span style={{ flex: 1, color: '#cbd5e1', fontSize: 12 }}>{c.name}</span>
              <span style={{ color: '#4b5563', fontSize: 12, fontWeight: 600 }}>{c.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ color: '#4b5563', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Documents récents</div>
        {stats.recent.length === 0 ? (
          <div style={{ background: '#ffffff', borderRadius: 8, padding: '20px', textAlign: 'center', color: '#4b5563', fontSize: 13 }}>
            Aucun document.{' '}
            <button onClick={() => goTab('upload')} style={{ color: '#1a9e72', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>
              Importer le premier
            </button>
          </div>
        ) : stats.recent.map(doc => (
          <div key={doc.id} style={{ background: '#ffffff', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20 }}>{doc.category_icon || '📄'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#111827', fontSize: 13, fontWeight: 500 }}>{doc.title}</div>
              <div style={{ color: '#4b5563', fontSize: 11 }}>{doc.category_name}{doc.linked_entity_name ? ` · ${doc.linked_entity_name}` : ''} · {fmtDate(doc.created_at)}</div>
            </div>
            <Badge status={doc.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── TAB 2 — Documents ─────────────────────────────────────────────────
function DocumentsTab({ companyId }) {
  const [docs, setDocs]         = useState([]);
  const [cats, setCats]         = useState([]);
  const [selected, setSel]      = useState(null);
  const [loading, setLoad]      = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [filters, setFilters]   = useState({ status: '', category_id: '', entity_type: '', search: '' });

  const load = useCallback(() => {
    setLoad(true);
    const p = { ...(companyId ? { company_id: companyId } : {}), ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
    api.ged.documents(p).then(r => setDocs(r.documents || [])).finally(() => setLoad(false));
  }, [companyId, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.ged.categories({}).then(setCats); }, []);

  const handleAnalyze = async (doc) => {
    setAnalyzing(true);
    try {
      const r = await api.ged.analyze(doc.id);
      setSel(prev => ({ ...prev, ai_summary: true, analysis: r.analysis }));
    } catch (e) { alert(e.message); }
    finally { setAnalyzing(false); }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archiver ce document ?')) return;
    await api.ged.deleteDocument(id);
    setSel(null);
    load();
  };

  return (
    <div style={{ display: 'flex', gap: 14, height: 'calc(100vh - 210px)', minHeight: 380 }}>
      <div style={{ flex: selected ? '0 0 54%' : '1', display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input placeholder="Rechercher…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            style={{ flex: 1, minWidth: 150, ...S.input, padding: '6px 10px', fontSize: 12 }} />
          {[
            ['status',      ['', 'Tous statuts'], ['actif', 'Actif'], ['expiré', 'Expiré'], ['résilié', 'Résilié'], ['en_attente', 'En attente']],
            ['category_id', ['', 'Toutes catégories'], ...cats.map(c => [String(c.id), `${c.icon} ${c.name}`])],
            ['entity_type', ['', 'Toutes entités'],   ...Object.entries(ENTITY_LABELS)],
          ].map(([key, ...opts]) => (
            <select key={key} value={filters[key]} onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
              style={{ ...S.input, width: 'auto', padding: '6px 8px', fontSize: 12 }}>
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}
          <button onClick={load} style={{ background: '#1a9e72', border: 'none', borderRadius: 6, color: '#fff', padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Filtrer</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', background: '#ffffff', borderRadius: 8 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#4b5563' }}>Chargement…</div>
          ) : docs.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#4b5563' }}>Aucun document.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['Titre', 'Catégorie', 'Entité', 'Expiration', 'Statut'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#4b5563', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => (
                  <tr key={doc.id} onClick={() => setSel(doc)} style={{ borderBottom: '1px solid #2d374840', cursor: 'pointer', background: selected?.id === doc.id ? '#eff6ff' : 'transparent' }}
                    onMouseEnter={e => { if (selected?.id !== doc.id) e.currentTarget.style.background = '#1a2235'; }}
                    onMouseLeave={e => { if (selected?.id !== doc.id) e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '9px 12px', color: '#111827', maxWidth: 200 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span>{doc.category_icon || '📄'}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</span>
                        {!!doc.is_confidential && <span title="Confidentiel" style={{ fontSize: 10 }}>🔒</span>}
                      </div>
                    </td>
                    <td style={{ padding: '9px 12px', color: '#4b5563' }}>{doc.category_name}</td>
                    <td style={{ padding: '9px 12px', color: '#4b5563' }}>{doc.linked_entity_name || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>{doc.expiry_date ? <ExpiryBadge date={doc.expiry_date} /> : <span style={{ color: '#4b5563' }}>—</span>}</td>
                    <td style={{ padding: '9px 12px' }}><Badge status={doc.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <div style={{ flex: '0 0 44%', background: '#ffffff', borderRadius: 10, padding: 20, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{selected.title}</div>
              <div style={{ color: '#4b5563', fontSize: 12, marginTop: 2 }}>
                {selected.category_icon} {selected.category_name}
                {selected.reference ? ` · Réf. ${selected.reference}` : ''}
              </div>
            </div>
            <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: '#4b5563', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              ['Statut',        <Badge status={selected.status} />],
              ['Entité',        selected.linked_entity_name ? `${ENTITY_LABELS[selected.linked_entity_type] || ''} : ${selected.linked_entity_name}` : '—'],
              ['Date doc.',     fmtDate(selected.document_date)],
              ['Entrée vigueur', fmtDate(selected.effective_date)],
              ['Expiration',    selected.expiry_date ? <ExpiryBadge date={selected.expiry_date} /> : '—'],
              ['Signature',     selected.requires_signature ? (selected.is_signed ? `✅ ${fmtDate(selected.signed_at)}` : '⏳ En attente') : 'Non requise'],
              ['Montant',       selected.contract_amount ? `${Number(selected.contract_amount).toLocaleString('fr-FR')} ${selected.currency || 'EUR'}` : '—'],
              ['Version',       `v${selected.version || 1}`],
              ['Taille',        formatBytes(selected.file_size)],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ color: '#4b5563', fontSize: 10, marginBottom: 2 }}>{label}</div>
                <div style={{ color: '#111827', fontSize: 12 }}>{val}</div>
              </div>
            ))}
          </div>

          {selected.description && (
            <div style={{ background: '#f9fafb', borderRadius: 6, padding: '10px 12px' }}>
              <div style={{ color: '#4b5563', fontSize: 10, marginBottom: 4 }}>Description</div>
              <div style={{ color: '#cbd5e1', fontSize: 12, lineHeight: 1.5 }}>{selected.description}</div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => downloadDocument(selected.id, selected.file_name)}
              style={{ background: '#185fa5', border: 'none', color: '#fff', padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
              Télécharger
            </button>
            {selected.file_type === 'application/pdf' && (
              <button onClick={() => handleAnalyze(selected)} disabled={analyzing}
                style={{ background: analyzing ? '#374151' : '#7c3aed', border: 'none', color: '#fff', padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: analyzing ? 'default' : 'pointer', fontWeight: 600 }}>
                {analyzing ? '⏳ Analyse…' : '🤖 Analyser IA'}
              </button>
            )}
            <button onClick={() => handleArchive(selected.id)}
              style={{ background: '#7f1d1d', border: 'none', color: '#fca5a5', padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
              Archiver
            </button>
          </div>

          {selected.analysis && (
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 14, border: '1px solid #4c1d95' }}>
              <div style={{ color: '#a78bfa', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>🤖 Analyse IA</div>
              {selected.analysis.resume && <p style={{ color: '#cbd5e1', fontSize: 12, lineHeight: 1.6, margin: '0 0 8px' }}><strong>Résumé :</strong> {selected.analysis.resume}</p>}
              {selected.analysis.parties?.length > 0 && <p style={{ color: '#cbd5e1', fontSize: 12, margin: '0 0 6px' }}><strong>Parties :</strong> {selected.analysis.parties.join(', ')}</p>}
              {selected.analysis.dates && (
                <div style={{ color: '#374151', fontSize: 12, margin: '0 0 6px' }}>
                  <strong>Dates :</strong>
                  {[['signature', 'Signature'], ['entree_en_vigueur', 'Entrée en vigueur'], ['expiration', 'Expiration']].map(([k, label]) => {
                    const d = selected.analysis.dates[k];
                    // Ancien format (chaîne) ou nouveau { valeur, statut }
                    const valeur = typeof d === 'string' ? d : d?.valeur;
                    const statut = typeof d === 'string' ? null : d?.statut;
                    return (
                      <div key={k} style={{ marginLeft: 8 }}>
                        {label} : {valeur || '—'}
                        {statut === 'calculée, à vérifier' && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>calculée, à vérifier</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              {selected.analysis.points_vigilance?.length > 0 && (
                <div style={{ color: '#fca5a5', fontSize: 12 }}>
                  <strong>Vigilance :</strong>
                  <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                    {selected.analysis.points_vigilance.map((p, i) => <li key={i} style={{ marginBottom: 2 }}>{p}</li>)}
                  </ul>
                </div>
              )}
              {selected.analysis.score_risque && (
                <div style={{ marginTop: 8 }}>
                  <strong style={{ color: '#4b5563', fontSize: 12 }}>Risque : </strong>
                  <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, background: selected.analysis.score_risque === 'élevé' ? '#7f1d1d' : selected.analysis.score_risque === 'moyen' ? '#7c2d12' : '#064e3b', color: selected.analysis.score_risque === 'élevé' ? '#fca5a5' : selected.analysis.score_risque === 'moyen' ? '#fdba74' : '#6ee7b7' }}>
                    {selected.analysis.score_risque}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── TAB 3 — Catégories ────────────────────────────────────────────────
function CategoriesTab({ companyId, goTab }) {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.ged.categories(companyId ? { company_id: companyId } : {}).then(setCats); }, [companyId]);

  return (
    <div>
      <div style={{ color: '#4b5563', fontSize: 12, marginBottom: 16 }}>
        {cats.length} catégories · {cats.reduce((s, c) => s + (c.doc_count || 0), 0)} documents au total
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
        {cats.map(cat => (
          <button key={cat.id} onClick={() => goTab('documents')}
            style={{ background: '#ffffff', border: `1px solid ${cat.color || '#4b5563'}30`, borderRadius: 10, padding: '14px 16px', textAlign: 'left', cursor: 'pointer', borderLeft: `3px solid ${cat.color || '#4b5563'}` }}
            onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
            onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{cat.icon || '📁'}</div>
            <div style={{ color: '#111827', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{cat.name}</div>
            {cat.description && <div style={{ color: '#4b5563', fontSize: 11, marginBottom: 8 }}>{cat.description}</div>}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ background: (cat.doc_count || 0) > 0 ? (cat.color || '#4b5563') + '25' : '#e5e7eb', color: (cat.doc_count || 0) > 0 ? (cat.color || '#4b5563') : '#4b5563', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                {cat.doc_count || 0} doc{cat.doc_count !== 1 ? 's' : ''}
              </span>
              {!!cat.has_expiry && <span title="Suivi expiration" style={{ fontSize: 10, color: '#f97316' }}>⏰</span>}
              {!!cat.requires_signature && <span title="Signature requise" style={{ fontSize: 10, color: '#8b5cf6' }}>✍️</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── TAB 4 — Agents commerciaux ────────────────────────────────────────
function AgentsTab({ companyId }) {
  const [agents, setAgents]       = useState([]);
  const [selected, setSel]        = useState(null);
  const [detail, setDetail]       = useState(null);
  const [mode, setMode]           = useState(null);
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);

  const load = useCallback(() => {
    api.ged.agents(companyId ? { company_id: companyId } : {}).then(setAgents);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (selected) api.ged.agent(selected.id).then(setDetail); else setDetail(null); }, [selected]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'create') await api.ged.createAgent({ ...form, company_id: companyId });
      else await api.ged.updateAgent(selected.id, form);
      setMode(null); setForm({}); load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const SC = { actif: '#22c55e', inactif: '#eab308', résilié: '#ef4444' };

  const FIELDS = [
    { k: 'name',            l: 'Nom *',                      req: true },
    { k: 'company_name',    l: 'Raison sociale' },
    { k: 'email',           l: 'Email',           t: 'email' },
    { k: 'phone',           l: 'Téléphone' },
    { k: 'siret',           l: 'SIRET' },
    { k: 'address',         l: 'Adresse',         full: true },
    { k: 'commission_rate', l: 'Commission (%)',   t: 'number' },
    { k: 'territory',       l: 'Zone géographique' },
    { k: 'product_lines',   l: 'Gammes produits' },
    { k: 'contract_start',  l: 'Début contrat',   t: 'date' },
    { k: 'contract_end',    l: 'Fin contrat',      t: 'date' },
  ];

  return (
    <div style={{ display: 'flex', gap: 14, height: 'calc(100vh - 210px)', minHeight: 380 }}>
      <div style={{ flex: (selected || mode === 'create') ? '0 0 36%' : '1', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#4b5563', fontSize: 13 }}>{agents.length} agent{agents.length !== 1 ? 's' : ''}</div>
          <button onClick={() => { setMode('create'); setForm({ status: 'actif' }); setSel(null); }}
            style={{ background: '#1a9e72', border: 'none', color: '#fff', padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            + Nouvel agent
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {agents.length === 0
            ? <div style={{ padding: 30, textAlign: 'center', color: '#4b5563', fontSize: 13 }}>Aucun agent enregistré.</div>
            : agents.map(a => (
              <div key={a.id} onClick={() => { setSel(a); setMode(null); }}
                style={{ background: selected?.id === a.id ? '#eff6ff' : '#ffffff', borderRadius: 8, padding: '12px 14px', marginBottom: 6, cursor: 'pointer', borderLeft: `3px solid ${SC[a.status] || '#4b5563'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ color: '#111827', fontWeight: 600, fontSize: 13 }}>{a.name}</div>
                  <span style={{ fontSize: 11, color: SC[a.status] || '#4b5563' }}>{a.status}</span>
                </div>
                <div style={{ color: '#4b5563', fontSize: 11, marginTop: 2 }}>
                  {[a.company_name, a.territory, a.commission_rate != null && `${a.commission_rate}%`].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))
          }
        </div>
      </div>

      {(selected || mode === 'create') && (
        <div style={{ flex: 1, background: '#ffffff', borderRadius: 10, padding: 20, overflow: 'auto' }}>
          {mode ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ color: '#111827', fontSize: 15, fontWeight: 700 }}>{mode === 'create' ? 'Nouvel agent' : `Modifier — ${selected?.name}`}</div>
                <button onClick={() => { setMode(null); setForm({}); }} style={{ background: 'none', border: 'none', color: '#4b5563', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {FIELDS.map(f => (
                  <div key={f.k} style={{ gridColumn: f.full ? '1/-1' : 'auto' }}>
                    <label style={S.label}>{f.l}</label>
                    <input type={f.t || 'text'} value={form[f.k] || ''} onChange={e => setForm(v => ({ ...v, [f.k]: e.target.value }))} style={S.input} />
                  </div>
                ))}
                <div>
                  <label style={S.label}>Notes</label>
                  <textarea value={form.notes || ''} onChange={e => setForm(v => ({ ...v, notes: e.target.value }))} rows={3}
                    style={{ ...S.input, resize: 'vertical' }} />
                </div>
                <div>
                  <label style={S.label}>Statut</label>
                  <select value={form.status || 'actif'} onChange={e => setForm(v => ({ ...v, status: e.target.value }))} style={S.input}>
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                    <option value="résilié">Résilié</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={handleSave} disabled={saving || !form.name}
                  style={{ background: '#1a9e72', border: 'none', color: '#fff', padding: '9px 20px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button onClick={() => { setMode(null); setForm({}); }}
                  style={{ background: '#e5e7eb', border: 'none', color: '#4b5563', padding: '9px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  Annuler
                </button>
              </div>
            </div>
          ) : detail ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{detail.name}</div>
                  <div style={{ color: '#4b5563', fontSize: 12 }}>{detail.company_name && `${detail.company_name} · `}<span style={{ color: SC[detail.status] }}>{detail.status}</span></div>
                </div>
                <button onClick={() => { setMode('edit'); setForm({ ...detail }); }}
                  style={{ background: '#185fa5', border: 'none', color: '#fff', padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Modifier</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                {[
                  ['Email', detail.email], ['Téléphone', detail.phone], ['SIRET', detail.siret],
                  ['Commission', detail.commission_rate != null ? `${detail.commission_rate}%` : null],
                  ['Zone', detail.territory], ['Gammes', detail.product_lines],
                  ['Début contrat', fmtDate(detail.contract_start)],
                  ['Fin contrat', detail.contract_end ? <ExpiryBadge date={detail.contract_end} /> : '—'],
                ].filter(([, v]) => v && v !== '—').map(([label, val]) => (
                  <div key={label} style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ color: '#4b5563', fontSize: 10, marginBottom: 2 }}>{label}</div>
                    <div style={{ color: '#111827', fontSize: 12 }}>{val}</div>
                  </div>
                ))}
              </div>
              {detail.notes && (
                <div style={{ background: '#f9fafb', borderRadius: 6, padding: '10px 12px', marginBottom: 14 }}>
                  <div style={{ color: '#4b5563', fontSize: 10, marginBottom: 4 }}>Notes</div>
                  <div style={{ color: '#cbd5e1', fontSize: 12 }}>{detail.notes}</div>
                </div>
              )}
              {detail.documents?.length > 0 && (
                <div>
                  <div style={{ color: '#4b5563', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Documents contractuels</div>
                  {detail.documents.map(doc => (
                    <div key={doc.id} style={{ background: '#f9fafb', borderRadius: 6, padding: '10px 12px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ color: '#111827', fontSize: 12 }}>{doc.category_icon} {doc.title}</div>
                        <div style={{ color: '#4b5563', fontSize: 11 }}>{doc.category_name} · {fmtDate(doc.document_date)}</div>
                      </div>
                      {doc.expiry_date && <ExpiryBadge date={doc.expiry_date} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : <div style={{ padding: 30, textAlign: 'center', color: '#4b5563' }}>Chargement…</div>}
        </div>
      )}
    </div>
  );
}

// ── TAB 5 — Upload wizard (5 étapes) ─────────────────────────────────
function UploadTab({ companyId, onSuccess }) {
  const [step, setStep]         = useState(1);
  const [file, setFile]         = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cats, setCats]         = useState([]);
  const [agents, setAgents]     = useState([]);
  const [form, setForm]         = useState({ status: 'actif', alert_days_before: 60, currency: 'EUR' });
  const [saving, setSaving]     = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    api.ged.categories({}).then(setCats);
    api.ged.agents(companyId ? { company_id: companyId } : {}).then(setAgents);
  }, [companyId]);

  const doUpload = async (f) => {
    setFile(f);
    if (!form.title) setForm(v => ({ ...v, title: f.name.replace(/\.[^.]+$/, '') }));
    setUploading(true);
    try {
      const r = await uploadFile(f);
      setUploaded(r);
      setForm(v => ({ ...v, stored_name: r.stored_name, file_name: r.file_name, file_size: r.file_size, file_type: r.file_type }));
      setStep(2);
    } catch (e) { alert(`Upload échoué : ${e.message}`); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.ged.createDocument({
        ...form,
        company_id: companyId,
        requires_signature: form.requires_signature ? 1 : 0,
        is_signed: form.is_signed ? 1 : 0,
        is_confidential: form.is_confidential ? 1 : 0,
        contract_amount: form.contract_amount ? parseFloat(form.contract_amount) : null,
      });
      setStep(6);
      if (onSuccess) onSuccess();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const field = (k, l, t = 'text') => (
    <div key={k}>
      <label style={S.label}>{l}</label>
      <input type={t} value={form[k] || ''} onChange={e => setF(k, e.target.value)} style={S.input} />
    </div>
  );
  const selField = (k, l, opts) => (
    <div key={k}>
      <label style={S.label}>{l}</label>
      <select value={form[k] || ''} onChange={e => setF(k, e.target.value)} style={S.input}>
        {opts.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
      </select>
    </div>
  );

  const STEPS = ['Fichier', 'Catégorie', 'Informations', 'Signature & Expiry', 'Confirmation'];

  if (step === 6) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: 50, marginBottom: 16 }}>✅</div>
      <div style={{ color: '#22c55e', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Document archivé</div>
      <div style={{ color: '#4b5563', marginBottom: 24 }}>{form.title}</div>
      <button onClick={() => { setStep(1); setFile(null); setUploaded(null); setForm({ status: 'actif', alert_days_before: 60, currency: 'EUR' }); }}
        style={{ background: '#1a9e72', border: 'none', color: '#fff', padding: '10px 24px', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
        Importer un autre document
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      {/* Progress bar */}
      <div style={{ display: 'flex', marginBottom: 28 }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, position: 'relative', zIndex: 1, background: step > i + 1 ? '#1a9e72' : step === i + 1 ? '#185fa5' : '#e5e7eb', color: step >= i + 1 ? '#fff' : '#4b5563' }}>
              {step > i + 1 ? '✓' : i + 1}
            </div>
            <div style={{ fontSize: 10, color: step === i + 1 ? '#111827' : '#4b5563', fontWeight: step === i + 1 ? 600 : 400 }}>{s}</div>
            {i < STEPS.length - 1 && <div style={{ position: 'absolute', top: 14, left: '50%', right: '-50%', height: 2, background: step > i + 1 ? '#1a9e72' : '#e5e7eb', zIndex: 0 }} />}
          </div>
        ))}
      </div>

      <div style={{ background: '#ffffff', borderRadius: 12, padding: 28 }}>
        {/* Step 1 — Drop zone */}
        {step === 1 && (
          uploading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
              <div style={{ color: '#4b5563' }}>Téléversement en cours…</div>
            </div>
          ) : (
            <div>
              <div style={{ color: '#111827', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Sélectionner le fichier</div>
              <div ref={dropRef} onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) doUpload(f); }} onDragOver={e => e.preventDefault()}
                style={{ border: '2px dashed #2d3748', borderRadius: 10, padding: '40px 20px', textAlign: 'center', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#1a9e72'} onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
                <div style={{ color: '#111827', fontSize: 14, marginBottom: 6 }}>Glissez-déposez votre fichier ici</div>
                <div style={{ color: '#4b5563', fontSize: 12, marginBottom: 16 }}>PDF, Word, Excel, images — 25 Mo max</div>
                <label style={{ background: '#185fa5', color: '#fff', padding: '9px 20px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                  Parcourir
                  <input type="file" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) doUpload(f); }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt" />
                </label>
              </div>
            </div>
          )
        )}

        {/* Step 2 — Category */}
        {step === 2 && (
          <div>
            <div style={{ color: '#111827', fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Catégorie du document</div>
            <div style={{ color: '#4b5563', fontSize: 12, marginBottom: 14 }}>Fichier : <strong style={{ color: '#4b5563' }}>{file?.name}</strong> ({formatBytes(uploaded?.file_size)})</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, maxHeight: 380, overflow: 'auto' }}>
              {cats.map(c => (
                <button key={c.id} onClick={() => { setF('category_id', c.id); setStep(3); }}
                  style={{ background: form.category_id === c.id ? '#185fa5' : '#f9fafb', border: `1px solid ${form.category_id === c.id ? '#185fa5' : '#e5e7eb'}`, borderRadius: 8, padding: '12px 10px', textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{c.icon}</div>
                  <div style={{ color: '#111827', fontSize: 12, fontWeight: 500 }}>{c.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 — Info */}
        {step === 3 && (
          <div>
            <div style={{ color: '#111827', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Informations du document</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {field('title',            'Titre *')}
              {field('reference',        'Référence / N° contrat')}
              {selField('linked_entity_type', "Type d'entité", [
                ['', '— Aucune —'], ...Object.entries(ENTITY_LABELS),
              ])}
              {field('linked_entity_name', "Nom de l'entité")}
              {form.linked_entity_type === 'agent_commercial' && selField('linked_entity_id', 'Agent commercial', [
                ['', '— Sélectionner —'], ...agents.map(a => [String(a.id), a.name]),
              ])}
              {field('document_date',    'Date du document',         'date')}
              {field('effective_date',   "Date d'entrée en vigueur", 'date')}
              {field('contract_amount',  'Montant (si applicable)',   'number')}
              {selField('currency', 'Devise', [['EUR', 'EUR — Euro'], ['USD', 'USD — Dollar'], ['GBP', 'GBP — Livre sterling'], ['CHF', 'CHF — Franc suisse']])}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={S.label}>Description</label>
                <textarea value={form.description || ''} onChange={e => setF('description', e.target.value)} rows={3}
                  style={{ ...S.input, resize: 'vertical' }} />
              </div>
              {field('tags', 'Tags (séparés par virgule)')}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="isconf" checked={!!form.is_confidential} onChange={e => setF('is_confidential', e.target.checked)} />
                <label htmlFor="isconf" style={{ color: '#cbd5e1', fontSize: 13, cursor: 'pointer' }}>Document confidentiel</label>
              </div>
            </div>
          </div>
        )}

        {/* Step 4 — Signature & expiry */}
        {step === 4 && (
          <div>
            <div style={{ color: '#111827', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Signature & Expiration</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {field('expiry_date',      "Date d'expiration",         'date')}
              {field('termination_date', 'Date de résiliation',       'date')}
              {field('alert_days_before', 'Alerte N jours avant',     'number')}
              {field('payment_terms',    'Modalités de paiement')}
              <div style={{ gridColumn: '1/-1', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#cbd5e1', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.requires_signature} onChange={e => setF('requires_signature', e.target.checked)} />
                  Signature requise
                </label>
                {form.requires_signature && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#cbd5e1', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.is_signed} onChange={e => setF('is_signed', e.target.checked)} />
                    Déjà signé
                  </label>
                )}
              </div>
              {form.requires_signature && form.is_signed && (
                <>
                  {field('signed_by', 'Signé par')}
                  {field('signed_at', 'Date de signature', 'date')}
                  {selField('signature_method', 'Méthode de signature', [
                    ['', '— Sélectionner —'], ['manuscrite', 'Manuscrite'], ['electronique', 'Électronique'], ['docusign', 'DocuSign'], ['yousign', 'YouSign'], ['autre', 'Autre'],
                  ])}
                </>
              )}
              {selField('status', 'Statut initial', [['actif', 'Actif'], ['en_attente', 'En attente']])}
            </div>
          </div>
        )}

        {/* Step 5 — Confirmation */}
        {step === 5 && (
          <div>
            <div style={{ color: '#111827', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Confirmation</div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  ['Fichier',      file?.name],
                  ['Taille',       formatBytes(uploaded?.file_size)],
                  ['Titre',        form.title],
                  ['Référence',    form.reference || '—'],
                  ['Catégorie',    cats.find(c => String(c.id) === String(form.category_id))?.name || '—'],
                  ['Entité',       form.linked_entity_name || '—'],
                  ['Date doc.',    fmtDate(form.document_date)],
                  ['Expiration',   fmtDate(form.expiry_date)],
                  ['Signature',    form.requires_signature ? (form.is_signed ? 'Signée' : 'Requise') : 'Non requise'],
                  ['Confidentiel', form.is_confidential ? 'Oui 🔒' : 'Non'],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: '#4b5563', fontSize: 12, flexShrink: 0 }}>{l} :</span>
                    <span style={{ color: '#111827', fontSize: 12 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={handleSave} disabled={saving || !form.title}
              style={{ background: '#1a9e72', border: 'none', color: '#fff', padding: '11px 28px', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 700, width: '100%' }}>
              {saving ? 'Enregistrement…' : '✅ Confirmer et archiver'}
            </button>
          </div>
        )}

        {/* Nav buttons */}
        {step > 1 && step < 6 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button onClick={() => setStep(s => s - 1)}
              style={{ background: '#e5e7eb', border: 'none', color: '#4b5563', padding: '8px 18px', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
              ← Retour
            </button>
            {step < 5 && (
              <button onClick={() => setStep(s => s + 1)} disabled={step === 3 && !form.title}
                style={{ background: '#185fa5', border: 'none', color: '#fff', padding: '8px 18px', borderRadius: 6, fontSize: 13, cursor: step === 3 && !form.title ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: step === 3 && !form.title ? 0.5 : 1 }}>
                Suivant →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── TAB 6 — Recherche avancée ─────────────────────────────────────────
function SearchTab({ companyId }) {
  const [form, setForm]       = useState({ q: '', category_id: '', status: '', entity_type: '' });
  const [results, setResults] = useState([]);
  const [loading, setLoad]    = useState(false);
  const [searched, setSearched] = useState(false);
  const [cats, setCats]       = useState([]);

  useEffect(() => { api.ged.categories({}).then(setCats); }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!form.q || form.q.length < 2) return;
    setLoad(true); setSearched(true);
    api.ged.search({ ...form, ...(companyId ? { company_id: companyId } : {}) })
      .then(setResults).finally(() => setLoad(false));
  };

  return (
    <div>
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        <input placeholder="Titre, référence, description, tags, entité…" value={form.q} onChange={e => setForm(v => ({ ...v, q: e.target.value }))}
          style={{ flex: 2, minWidth: 220, ...S.input }} />
        <select value={form.category_id} onChange={e => setForm(v => ({ ...v, category_id: e.target.value }))} style={{ ...S.input, width: 'auto' }}>
          <option value="">Toutes catégories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
        <select value={form.status} onChange={e => setForm(v => ({ ...v, status: e.target.value }))} style={{ ...S.input, width: 'auto' }}>
          <option value="">Tous statuts</option>
          <option value="actif">Actif</option>
          <option value="expiré">Expiré</option>
          <option value="résilié">Résilié</option>
          <option value="en_attente">En attente</option>
        </select>
        <select value={form.entity_type} onChange={e => setForm(v => ({ ...v, entity_type: e.target.value }))} style={{ ...S.input, width: 'auto' }}>
          <option value="">Toutes entités</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button type="submit" style={{ background: '#1a9e72', border: 'none', color: '#fff', padding: '9px 20px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
          Rechercher
        </button>
      </form>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#4b5563' }}>Recherche…</div>}
      {!loading && searched && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#4b5563', fontSize: 13 }}>Aucun résultat pour « {form.q} »</div>
      )}
      {results.length > 0 && (
        <div>
          <div style={{ color: '#4b5563', fontSize: 12, marginBottom: 12 }}>{results.length} résultat{results.length > 1 ? 's' : ''}</div>
          {results.map(doc => (
            <div key={doc.id} style={{ background: '#ffffff', borderRadius: 8, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, borderLeft: `3px solid ${doc.category_color || '#4b5563'}` }}>
              <span style={{ fontSize: 22 }}>{doc.category_icon || '📄'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#111827', fontWeight: 600, fontSize: 13 }}>
                  {doc.title}{doc.is_confidential ? <span style={{ marginLeft: 6, fontSize: 11 }}>🔒</span> : null}
                </div>
                <div style={{ color: '#4b5563', fontSize: 11, marginTop: 2 }}>
                  {[doc.category_name, doc.linked_entity_name, doc.reference && `Réf. ${doc.reference}`, `Ajouté ${fmtDate(doc.created_at)}`].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                {doc.expiry_date && <ExpiryBadge date={doc.expiry_date} />}
                <Badge status={doc.status} />
                <button onClick={() => downloadDocument(doc.id, doc.file_name)}
                  style={{ background: '#185fa5', border: 'none', color: '#fff', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                  Ouvrir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard',  label: 'Tableau de bord', icon: '📊' },
  { id: 'documents',  label: 'Documents',        icon: '📁' },
  { id: 'categories', label: 'Catégories',       icon: '🗂️' },
  { id: 'agents',     label: 'Agents commerciaux', icon: '🤝' },
  { id: 'upload',     label: 'Importer',         icon: '⬆️' },
  { id: 'search',     label: 'Recherche',        icon: '🔍' },
];

export default function DocumentsArchive({ currentCompanyId, currentSociete }) {
  const [tab, setTab] = useState('dashboard');

  return (
    <div style={{ padding: '0 0 40px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#111827', fontSize: 22, fontWeight: 800, margin: 0 }}>Espace documentaire & GED</h1>
        <div style={{ color: '#4b5563', fontSize: 13, marginTop: 4 }}>
          {currentSociete && <><strong style={{ color: '#4b5563' }}>{currentSociete}</strong> · </>}
          Archivage sécurisé · Contrats · Agents commerciaux
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ background: 'none', border: 'none', padding: '10px 16px', cursor: 'pointer', color: tab === t.id ? '#111827' : '#4b5563', fontSize: 13, fontWeight: tab === t.id ? 700 : 400, borderBottom: tab === t.id ? '2px solid #1a9e72' : '2px solid transparent', whiteSpace: 'nowrap' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard'  && <DashboardTab  companyId={currentCompanyId} goTab={setTab} />}
      {tab === 'documents'  && <DocumentsTab  companyId={currentCompanyId} />}
      {tab === 'categories' && <CategoriesTab companyId={currentCompanyId} goTab={setTab} />}
      {tab === 'agents'     && <AgentsTab     companyId={currentCompanyId} />}
      {tab === 'upload'     && <UploadTab     companyId={currentCompanyId} onSuccess={() => setTab('documents')} />}
      {tab === 'search'     && <SearchTab     companyId={currentCompanyId} />}
    </div>
  );
}
